import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import $ from 'jquery';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Packing_Checklist = ({ show, onClose, packingData }) => {
  const onInit = () => {};
  const tableRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const BaseUrl = process.env.REACT_APP_API_BASE_URL;
  const branch_id = parseInt(localStorage.getItem('branch_id'), 10) || 0;

  // Map packingData to rows
  useEffect(() => {
    if (packingData?.item_list?.length) {
      const newRows = packingData.item_list.map((item) => ({
        id: item.item_id,
        name: item.item_name,
        image: item.item_image,
        cookedQuantity: item.cooked_quantity ?? 0,
        cookedUnit: item.cooked_unit ?? '',
        packingQuantity: item.packing_quantity ?? 0,
        packingUnit: item.packing_unit ?? '',
        baseQuantity: item.base_quantity ?? 0,
        baseUnit: item.base_unit_name ?? '',
        isChecked: item.status === 1,
        checkedFromDB: item.status === 1,
        wastageQty: item.wasted_qty ?? 0,
        reworkQty: item.required_qty ?? 0,
        reason: item.reason || '',
        selectedOption: '', // default radio selection
        statusLabel: item.item_list_status_label || 'Pending',
      }));
      setRows(newRows);
      setSelectAll(newRows.every(r => r.isChecked));
    } else {
      setRows([]);
      setSelectAll(false);
    }
  }, [packingData]);

  // Initialize DataTable
  useEffect(() => {
    if (tableRef.current && !$.fn.dataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable({
        responsive: true,
        ordering: false,
        paging: false,
        language: { lengthMenu: 'Show _MENU_' },
        dom: "<'row'><'table-responsive'tr><'row'>",
      });
    }
  }, [rows]);

  // Checkbox handlers
  const handleCheckboxChange = (id, checked) => {
    setRows((prev) => prev.map((row) => row.id === id ? { ...row, isChecked: checked } : row));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setRows((prev) => prev.map((row) => ({ ...row, isChecked: checked })));
  };

  // Input handlers
  const handleInputChange = (id, field, value) => {
    setRows((prev) => prev.map((row) => row.id === id ? { ...row, [field]: value } : row));
  };

  const handleRadioChange = (id, option) => {
    setRows((prev) =>
      prev.map((row) => row.id === id ? { ...row, selectedOption: option } : row)
    );
  };

const handleSubmit = async () => {
  const itemsData = rows
    .filter(row => row.isChecked)
    .map(row => ({
      id: row.id, // must match DB item_id
      action: row.selectedOption || null,
      quantity:
        row.selectedOption === 'Wastage'
          ? row.wastageQty
          : row.selectedOption === 'Rework'
          ? row.reworkQty
          : null,
      reason: row.reason || '',
    }));

  if (!itemsData.length) {
    toast.error('Please select at least one item to submit.');
    return;
  }

  const payload = {
    branch: branch_id,
    packing_id: packingData?.sno ?? 0,
    items: itemsData,
  };

  try {
    await axios.post(`${BaseUrl}/packing/packing_checklist`, payload);
    toast.success('Packing checklist submitted successfully!');
    onClose();
  } catch (error) {
    toast.error('Failed to submit packing checklist.');
    console.error(error.response?.data || error.message);
  }
};


  if (!packingData) return null;

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <div className="modal-content border border-red rounded-xxl">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Packing Checklist</h3>
          <Button
            variant=""
            type="button"
            className="close px-6 py-2"
            data-dismiss="modal"
            onClick={onClose}
          >
            <span className="text-white">×</span>
          </Button>
        </div>
        <div className="modal-body">
          <div className="row mb-4">
            <div className="col-lg-4 d-flex flex-row align-items-center justify-content-start gap-2 mb-2">
              <LightGallery
                onInit={onInit}
                speed={500}
                plugins={[lgThumbnail, lgZoom]}
              >
                <div data-src={packingData?.product_image} className="border rounded px-1 py-0">
                  <img
                    src={packingData?.product_image}
                    alt={packingData?.product_name || 'Product'}
                    className="cursor-pointer w-60px h-60px"
                  />
                </div>
              </LightGallery>
              <div className="d-flex flex-column align-items-start justify-content-start">
                <label className="text-danger font-w600 fs-5">
                  {packingData?.product_name || 'Product Name'}
                </label>
                <label className="fs-7 text-dark">{packingData?.package_name}</label>
                <label className="fs-7 text-black">{packingData?.product_category_name}</label>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="d-flex flex-column align-items-start justify-content-start">
                <label className="text-black font-w600 fs-5">Total Box Count</label>
                <label className="fs-5 text-primary font-w500 border border-primary py-1 px-2 rounded text-center">
                  {(String(packingData?.subscriber_count ?? 0)).padStart(2, '0')}
                </label>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="d-flex flex-column align-items-start justify-content-start">
                <label className="text-black font-w600 fs-5">Packing Counts</label>
                <label className="fs-5 text-danger font-w500 border border-danger py-1 px-2 rounded text-center">
                  {(String(packingData?.packed_item_count ?? 0)).padStart(2, '0')} / {(String(packingData?.total_packing_count ?? 0)).padStart(2, '0')}
                </label>
              </div>
            </div>
          </div>

          <div className="row mb-2">
            <table className="table align-middle table-row-dashed table-striped table-hover fs-7 gy-1 gs-2 dataTable">
              <thead>
                <tr className=" text-start text-muted fw-bold fs-6 gs-0 th_head">
                  <th className="min-w-50px">
                    <div className="form-check custom-checkbox"> 
                      <input
                        type="checkbox"
                        className="form-check-input me-3"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </div>
                  </th>
                  <th className="min-w-75px">Item</th>
                  <th className="min-w-50px text-center">Cooked Quantity</th>
                  <th className="min-w-50px text-center">Per Box Quantity</th>
                  <th className="min-w-150px text-center">Pending Work</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 fw-semibold fs-7">
                {rows.map((row) => (
                  <tr key={row.id} className={row.checkedFromDB ? 'bg-light text-muted' : ''}>
                    <td>
                      <div className="form-check custom-checkbox">
                        <input
                          type="checkbox"
                          className="form-check-input me-3"
                          checked={row.isChecked}
                          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
                          disabled={row.checkedFromDB}
                        />
                      </div>
                    </td>
                    <td>
                      <label className="text-black font-w400 fs-7">{row.name}</label>
                    </td>
                    <td className="text-center">
                      <label className="text-black font-w400 fs-7">{row.cookedQuantity} {row.cookedUnit}</label>
                    </td>
                    <td className="text-center">
                      <label className="text-black font-w400 fs-7">{row.baseQuantity} {row.baseUnit}</label>
                    </td>
                    <td className="text-center">
                      <div className="d-flex gap-3 mb-2 justify-content-center">
                        <label className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            name={`work-${row.id}`}
                            checked={row.selectedOption === 'Wastage'}
                            onChange={() => handleRadioChange(row.id, 'Wastage')}
                            disabled={row.isChecked || row.checkedFromDB}
                          />
                          Wastage
                        </label>
                        <label className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            name={`work-${row.id}`}
                            checked={row.selectedOption === 'Rework'}
                            onChange={() => handleRadioChange(row.id, 'Rework')}
                            disabled={row.isChecked || row.checkedFromDB}
                          />
                          Rework
                        </label>
                      </div>

                      {row.selectedOption && !row.isChecked && !row.checkedFromDB && (
                        <div className="d-flex gap-2 justify-content-center align-items-end">
                          <input
                            type="number"
                            className="form-control"
                            style={{ width: '120px' }}
                            placeholder="Quantity"
                            value={row.selectedOption === 'Wastage' ? row.wastageQty : row.reworkQty}
                            onChange={(e) =>
                              handleInputChange(
                                row.id,
                                row.selectedOption === 'Wastage' ? 'wastageQty' : 'reworkQty',
                                e.target.value
                              )
                            }
                          />
                          <input
                            type="text"
                            className="form-control"
                            style={{ width: '200px' }}
                            placeholder="Reason"
                            value={row.reason}
                            onChange={(e) => handleInputChange(row.id, 'reason', e.target.value)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mb-4">
            <Button variant="primary" onClick={handleSubmit}>
              Submit Packing Checklist
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Packing_Checklist;
